/**
 * Authentication Provider for Refine
 *
 * Implements Authentik OIDC-based authentication with cookie sessions.
 * Follows Refine AuthProvider interface for seamless integration.
 *
 * Authentication Flow:
 * 1. login() - Redirects to /auth/login which redirects to Authentik
 * 2. Authentik handles authentication and redirects to /auth/callback
 * 3. Backend sets session cookie (HttpOnly, Secure)
 * 4. check() - Validates session by calling /auth/me
 * 5. logout() - Clears session cookie via /auth/logout
 *
 * Requirements: 1.1-1.7 (Authentik authentication, dual auth, session management)
 */

import type { AuthProvider } from "@refinedev/core";
import { AuthenticationService } from "../api-client";
import type { UserInfoResponse } from "../api-client";

/**
 * Shared promise for /auth/me requests to prevent duplicate calls.
 * When multiple methods (check, getIdentity) call /auth/me simultaneously,
 * they will share the same request instead of making separate ones.
 */
let authMeRequest: Promise<UserInfoResponse> | null = null;
let authMeCache: { response: UserInfoResponse; timestamp: number } | null = null;
const AUTH_ME_CACHE_TTL = 1000; // Cache for 1 second to prevent rapid duplicate calls
let cacheClearedAt = 0; // Timestamp when cache was last cleared

/**
 * Get current user from /auth/me endpoint with request deduplication.
 * 
 * This function ensures that if multiple calls to /auth/me happen simultaneously,
 * only one actual HTTP request is made, and all callers share the same result.
 * 
 * @returns Promise with user info or null if not authenticated
 */
async function getCurrentUserWithDeduplication(): Promise<UserInfoResponse | null> {
  // Check cache first (very short TTL to prevent rapid duplicate calls)
  // Ignore cache if it was explicitly cleared after being set
  if (authMeCache &&
      Date.now() - authMeCache.timestamp < AUTH_ME_CACHE_TTL &&
      authMeCache.timestamp > cacheClearedAt) {
    console.log('[AuthProvider] Using cached auth result');
    return authMeCache.response;
  }

  // If there's already a request in progress, reuse it
  if (authMeRequest) {
    console.log('[AuthProvider] Reusing existing auth request');
    try {
      const response = await authMeRequest;
      return response;
    } catch (error) {
      // If the shared request failed, clear it and re-throw
      authMeRequest = null;
      throw error;
    }
  }

  // Start new request
  console.log('[AuthProvider] Starting new auth request');
  authMeRequest = AuthenticationService.getCurrentUserAuthMeGet({})
    .then((response) => {
      // Cache successful response
      authMeCache = { response, timestamp: Date.now() };
      authMeRequest = null;
      return response;
    })
    .catch((error) => {
      // Clear request on error
      authMeRequest = null;
      throw error;
    });

  // Let errors propagate to caller
  const response = await authMeRequest;
  return response;
}

/**
 * Clear auth cache (useful after logout or when session changes)
 */
export function clearAuthCache(): void {
  cacheClearedAt = Date.now();
  authMeCache = null;
  authMeRequest = null;
}

/**
 * Authentication provider for Refine framework.
 * Handles cookie-based session authentication with Authentik OIDC.
 */
export const authProvider: AuthProvider = {
  /**
   * Initiate login flow by redirecting to authentication endpoint.
   *
   * The /auth/login endpoint will redirect to Authentik OAuth provider.
   * After successful authentication, Authentik redirects to /auth/callback
   * which sets the session cookie and redirects back to the app.
   *
   * @returns Promise resolving to success (though redirect happens before resolution)
   */
  login: async () => {
    // Redirect to backend auth endpoint which handles Authentik OAuth flow
    window.location.href = `${import.meta.env.VITE_API_URL || 'http://localhost:8000'}/auth/login`;

    // Return success (though user will be redirected before this resolves)
    return {
      success: true,
    };
  },

  /**
   * Logout user by clearing session cookie.
   *
   * Calls backend /auth/logout endpoint which clears the session cookie
   * and CSRF token. Backend returns redirect URL but we handle redirect here.
   *
   * @returns Promise with logout success and redirect path
   */
  logout: async () => {
    try {
      // Clear auth cache before logout
      clearAuthCache();
      
      // Call logout endpoint to clear session cookie
      await AuthenticationService.logoutAuthLogoutPost();

      return {
        success: true,
        redirectTo: "/login",
      };
    } catch (error) {
      console.error("Logout error:", error);

      // Clear cache even if API call fails
      clearAuthCache();

      // Even if API call fails, redirect to login
      return {
        success: true,
        redirectTo: "/login",
      };
    }
  },

  /**
   * Check if user is authenticated.
   *
   * Validates session by calling /auth/me endpoint which checks session cookie.
   * Session cookies are automatically included by browser (credentials: 'include').
   *
   * @returns Promise indicating authentication status
   */
  check: async () => {
    // If we're already on the login page, don't check authentication
    // This prevents infinite redirect loops when user is not authenticated
    const isLoginPage = typeof window !== 'undefined' && window.location?.pathname?.includes("/login");
    
    if (isLoginPage) {
      console.log('[AuthProvider] Already on login page, skipping auth check');
      return {
        authenticated: false,
        // Don't redirect - we're already on login page
      };
    }

    try {
      console.log('[AuthProvider] Checking authentication...');
      // Use deduplicated request to /auth/me
      const response = await getCurrentUserWithDeduplication();
      
      if (response && response.authenticated) {
        console.log('[AuthProvider] User authenticated!');
        return {
          authenticated: true,
        };
      } else {
        console.warn('[AuthProvider] User not authenticated');
        return {
          authenticated: false,
          redirectTo: "/login",
          error: {
            message: "Session invalid",
            name: "Unauthorized",
          },
        };
      }
    } catch (error: any) {
      // Session invalid or expired
      console.error("[AuthProvider] Authentication check failed:", error);

      // Only redirect if we're not already on login page
      return {
        authenticated: false,
        redirectTo: isLoginPage ? undefined : "/login",
        error: {
          message: error?.message || "Authentication failed",
          name: "Unauthorized",
        },
      };
    }
  },

  /**
   * Handle authentication errors.
   *
   * Called by Refine when API requests return 401/403.
   * Triggers logout flow to clear invalid session.
   *
   * @param error - Error object from failed request
   * @returns Promise indicating whether to logout
   */
  onError: async (error: any) => {
    // Check if error is authentication-related
    if (error?.statusCode === 401 || error?.statusCode === 403) {
      return {
        logout: true,
        redirectTo: "/login",
        error: {
          message: error?.message || "Session expired",
          name: "Unauthorized",
        },
      };
    }

    // For other errors, don't trigger logout
    return {};
  },

  /**
   * Get current user identity.
   *
   * Fetches authenticated user information from /auth/me endpoint.
   * Used by Refine to display user info in UI (username, email, etc).
   *
   * @returns Promise with user identity data
   */
  getIdentity: async (): Promise<UserInfoResponse | null> => {
    // If we're on the login page, don't try to get identity
    // This prevents unnecessary API calls during login flow
    const isLoginPage = typeof window !== 'undefined' && window.location?.pathname?.includes("/login");
    
    if (isLoginPage) {
      console.log('[AuthProvider] On login page, skipping getIdentity');
      return null;
    }

    try {
      // Use deduplicated request to /auth/me
      const response = await getCurrentUserWithDeduplication();

      if (response && response.authenticated) {
        return {
          user_id: response.user_id,
          email: response.email || null,
          name: response.name || response.email || response.user_id,
          authenticated: true,
        };
      }

      return null;
    } catch (error) {
      console.error("Failed to get user identity:", error);
      return null;
    }
  },

  /**
   * Get permissions for current user.
   *
   * Currently not implemented as all authenticated admin users have full access.
   * Future enhancement: Implement role-based access control.
   *
   * @returns Promise with permissions (empty for now)
   */
  getPermissions: async () => {
    // Future: Implement role-based permissions
    // For now, all authenticated users have admin access
    return null;
  },
};
